import React, {useContext, useEffect, useState} from 'react';
import {Alert, Button, Checkbox, Descriptions, Form, FormInstance, Input, notification, Space, Upload} from 'antd';
import type { UploadProps } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import {FileHandler} from "../../../handler/file.handler";
import styled from "styled-components";
import {observer} from "mobx-react";
import {StoreContext} from "../../../store.context";
import {Translate} from "../../../components/util/translation.util";
import {CardStyled} from "../../../components/styled/card.styled";
import {ButtonBar} from "../../../builder";
import ExportButton from "../../../components/button/export.button";
import {RequiredConstraint} from "../../../builder/form/validation/constraints";
import {Prompt} from "../../../components/promptBlocker/promptBlocker";

const DescriptionsStyled = styled(Descriptions)`
    .ant-descriptions-item-label {
        width: 40%;
    }
`;

const { Dragger } = Upload;

const layout = {
    labelCol: { span: 4 },
    wrapperCol: { span: 20 },
    style: { padding: 16, paddingBottom: 0 }
};

export const ImportForm: React.FC = observer(() => {
    const {
        appStore: { translate, moduleStore: { translationStore} }
    } = useContext(StoreContext);

    const formRef = React.createRef<FormInstance>();

    const [ translations, setTranslations ] = useState<any>({
        newTranslations: [],
        skippedTranslations: [],
        existingTranslations: [],
    });

    const [ formStateDirty, setFormStateDirty] = useState<boolean>(false);
    const [ importErrors, setImportErrors ] = useState<any>({});

    const props: UploadProps = {
        name: 'file',
        multiple: false,
        showUploadList: false,
        accept: "application/json",
        customRequest: async (options) => {
            FileHandler.readJsonContents(options.file, (json) => {
                translationStore.import(json).then((response: any) => {
                    setFormStateDirty(true);
                    setTranslations(response.data);
                }).catch((error: any) => {
                    setImportErrors(error.response.data.errors)
                })
            }, () => {
                notification.open({
                    message: `Invalid file`,
                    description: 'File invalid, please upload correct JSON file.',
                    type: "error"
                });
            })
        }
    };

    useEffect(() => {
        translationStore.getImport().then((response: any) => {
            setFormStateDirty(response.data.existingTranslations.length > 0);
            setTranslations(response.data);
        });
    }, [translationStore]);

    useEffect(() => {
        formRef.current?.setFieldsValue(translations)
    }, [formRef, translations])

    const onFinish = (values: any) => {
        translationStore.saveImport({...translations, ...values})
            .then((response: any) => {
                setFormStateDirty(false);
                setTranslations(response.data);
            });
    }

    const checkAll = (checked: boolean) => {
        translations.existingTranslations.map( (translation: any) => translation.selected = checked);
        formRef.current?.setFieldsValue(translations)
    }

    return (
        <>
            <CardStyled title={<Translate message={`global.title.import`} />}>
                {
                    <Prompt
                        when={ formStateDirty }
                        message={ translate('global.unsaved_changes_leave') }
                        beforeUnload={ true }
                    />
                }

                <Form {...layout} ref={formRef} name="control-ref" onFinish={onFinish} initialValues={translations}>
                    <Space direction={`vertical`} style={{ display: 'flex' }}>
                        <Dragger {...props}>
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <div className="ant-upload-text"><Translate message={`translation.import.input_title`} /></div>
                            <div className="ant-upload-hint">
                                <Translate message={`translation.import.input_description`} />
                            </div>
                        </Dragger>

                        {
                            Object.keys(importErrors).map(error => {
                                return <Alert message={ error } description={ importErrors[error] } type="error" />
                            })
                        }

                        { translations.existingTranslations.length > 0 && (
                            <Space direction={`vertical`} style={{ display: 'flex' }}>
                                <DescriptionsStyled bordered>
                                    <Descriptions.Item span={1} label={<b><Translate message={`translation.import.new_translation`} /></b>}>
                                        <b>{ translations.newTranslations.length }</b>
                                    </Descriptions.Item>
                                    <Descriptions.Item span={1} label={<b><Translate message={`translation.import.skipped_translations`} /></b>}>
                                        <b>{ translations.skippedTranslations.length }</b>
                                    </Descriptions.Item>
                                </DescriptionsStyled>

                                <Checkbox onChange={(e) => {
                                    checkAll(e.target.checked)
                                }}><Translate message={'global.check_all'} /></Checkbox>

                                <Form.List name="existingTranslations">
                                    {(existingTranslations) => {
                                        return <DescriptionsStyled bordered>
                                            {existingTranslations.map((translation, i) => {
                                                return (
                                                    <Descriptions.Item key={translation.key} span={3} label={
                                                        <Form.Item {...translation} valuePropName={`checked`} name={[translation.name, 'selected']}>
                                                            <Checkbox>
                                                                { translations.existingTranslations[translation.key].key }
                                                                &nbsp;({ translations.existingTranslations[translation.key].domain })
                                                                - { translations.existingTranslations[translation.key].locale }
                                                            </Checkbox>
                                                        </Form.Item>
                                                    }>
                                                        <Form.Item name={[translation.name, 'content']} label={
                                                            <Translate message={`translation.import.new_value`} />
                                                        } rules={[ RequiredConstraint() ]}>
                                                            <Input />
                                                        </Form.Item>
                                                        <Form.Item  name={[translation.name, 'dbValue']} label={
                                                            <Translate message={`translation.import.old_value`} />
                                                        }>
                                                            <Input disabled={true} />
                                                        </Form.Item>
                                                    </Descriptions.Item>
                                                )
                                            })}
                                        </DescriptionsStyled>
                                    }}
                                </Form.List>
                            </Space>
                        ) }
                    </Space>
                </Form>
            </CardStyled>
            <ButtonBar>
                <Button type="primary" onClick={ () => formRef.current?.submit() }>
                    <Translate message={`global.button.save`} />
                </Button>
                <ExportButton exporter={ translationStore.getExport } fileNamePrefix={(new Date()).toISOString() + '_alias_translation'} />
            </ButtonBar>
        </>
    )
})